import { stripeSubscriptionStorageKey } from "@Containers/web-portal/utils/stripe";
import { AppContext, Events } from "@Context/common/general/context";
import AccountRoles from "@Enums/AccountRoles";
import { useApi } from "@hooks/index";
import useI18n from "@i18n";
import { Button } from "@mui/material";
import { Api } from "@Shared/schemas";
import { useCallback, useContext } from "react";
import { useSearchParams } from "react-router-dom";
import { defineNotificationModule, INotificationResolverProps } from "../notification-service.types";
import { useExclusiveMode } from "../shared/use-exclusive-mode";
import { useNotificationActions } from "../shared/use-notification-actions";
import { useRecoveryNotification } from "../shared/use-recovery-notification";

const id = "ACCOUNT_PRODUCTS_RECOVERY";

const AccountProductsRecoveryNotificationResolver = ({ priority }: INotificationResolverProps) => {
  const { dispatch, state } = useContext(AppContext);
  const { i18n } = useI18n();
  const { handleSelectAccount } = useNotificationActions();
  const isExclusiveMode = useExclusiveMode();
  const [searchParams] = useSearchParams();
  const hasSubscriptionSuccess = Boolean(searchParams.get(stripeSubscriptionStorageKey));
  const userDbId = state.user.dbId;
  const accountId = state.account?.id;
  const isAccountOwner = state.account?.user_role === AccountRoles.ACCOUNT_OWNER;

  const { call: recoverAccount } = useApi<typeof Api.Account.RecoverAccount>({
    api: Api.Account.RecoverAccount,
    request: {
      user_id: state.user.dbId!,
      account_id: state.account.id!,
    },
  });

  const recoveryRequest = useCallback(async () => {
    if (!userDbId || !accountId) {
      return;
    }

    await recoverAccount();
  }, [recoverAccount, userDbId, accountId]);

  const handleRecovered = useCallback(() => {
    Events.account.refreshCurrentAccount.emit();
  }, [dispatch]);

  useRecoveryNotification({
    actionKey: "account.recovery.products.action",
    hideActionButton: !isAccountOwner,
    secondaryActions: (
      <Button variant="outlined" fullWidth onClick={handleSelectAccount}>
        {i18n("button.change.account")}
      </Button>
    ),
    delayMs: 2000,
    id,
    isActive: state.user.needsAccountProductsRecovery && !isExclusiveMode && !hasSubscriptionSuccess,
    loadingKey: "account.recovery.products.loading",
    messageKey: isAccountOwner ? "account.recovery.products.message.owner" : "account.recovery.products.message.member",
    onRecovered: handleRecovered,
    priority,
    recoveryRequest,
    titleKey: "account.recovery.products.title",
  });

  return null;
};

export const accountProductsRecoveryNotification = defineNotificationModule({
  id,
  Resolver: AccountProductsRecoveryNotificationResolver,
});
